import {
  FC,
  SyntheticEvent,
  MouseEvent,
  useCallback,
  useMemo,
  useState,
  useEffect,
  memo,
} from 'react';
import { BellOff } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router';

import { APP_ROUTES } from '@navigation/types';
import { useModal } from '@hooks/useModal';
import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useUnauthorizedModal } from '@hooks/useUnauthorizedModal';
import { useChannelDataById } from '@redux/selectors/channels';
import { useShowDataById } from '@redux/selectors/shows';
import {
  useLeftTeamSpeakerId,
  useRightTeamSpeakerId,
  useSpeakerDataById,
} from '@redux/selectors/speakers';
import { useCurrentUser, useUserDataById } from '@redux/selectors/users';
import { useAuthUser } from '@redux/selectors/auth';
import theme from 'theme';
import { AvatarSize } from '@typings/user';
import { ShowStatus, ShowType } from '@typings/show';
import { ParticipantStatus } from '@typings/participant';
import {
  getDateTimeString,
  getListenersText,
  formatDuration,
} from '@utils/index';
import Avatar from '@components/avatar';
import liveAnimation from '@assets/animations/live-now.json';
import cardImage from '@assets/images/card.png';
import Modal from '@components/modal';
import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { useRecordedShow } from '@providers/RecordedShowProvider';
import { PremierText } from '@components/text';
import { useCoHostData } from '@redux/selectors/participants';

import * as Styled from './styled';
import Countdown from './components/Countdown';
import Sides from './components/Sides';
import SkewedLabel from './components/SkewedLabel';
import UserNameBlock from './components/UserNameBlock';

type Props = {
  showId: string;
};

const ShowCard: FC<Props> = ({ showId }) => {
  const {
    hostId,
    coHostId,
    title,
    status,
    listenersCount,
    image,
    recorded,
    createdDate,
    channelId,
    recordDuration,
    scheduledShow,
    blueSide,
    redSide,
    type,
  } = useShowDataById(showId);
  const [showImage, setShowImage] = useState<string>(image || ' ');
  const [isWaitingNavigation, setIsWaitingNavigation] =
    useState<boolean>(false);
  const duration = useMemo(
    () => recordDuration && formatDuration(Number(recordDuration)),
    [recordDuration],
  );
  const { currentShowId, onFinishOrLeaveCurrentShow } = useAgoraEngine();
  const { currentRecordedShowId, closeCurrentRecordedShow } = useRecordedShow();
  const currentUser = useCurrentUser();
  const coHostData = useCoHostData(showId);
  const channelData = useChannelDataById(channelId);
  const rightSpeakerId = useRightTeamSpeakerId(showId);
  const leftSpeakerId = useLeftTeamSpeakerId(showId);
  const rightSpeakerProfileId = useSpeakerDataById(rightSpeakerId)?.profileId;
  const leftSpeakerProfileId = useSpeakerDataById(leftSpeakerId)?.profileId;
  const hostProfileData = useUserDataById(hostId);
  const coHostProfileData = useUserDataById(coHostId);
  const isRecord = useMemo(
    () => recorded && status === ShowStatus.Finished,
    [recorded, status],
  );
  const isScheduled = useMemo(
    () => !!scheduledShow?.id && status === ShowStatus.Scheduled,
    [scheduledShow?.id, status],
  );
  const isHost = useMemo(
    () => hostId === currentUser?.id,
    [hostId, currentUser?.id],
  );
  const isShowCoHost = useMemo(
    () =>
      isRecord
        ? coHostId && coHostData?.status !== ParticipantStatus.Pending
        : coHostId &&
          coHostData?.status !== ParticipantStatus.Pending &&
          coHostData?.status !== ParticipantStatus.Left,
    [coHostId, coHostData?.status, isRecord],
  );
  const navigate = useNavigate();
  const { isModalShown, showModal, hideModal } = useModal();
  const { openDownloadAppModal } = useDownloadAppModal();
  const { openUnauthorizedModal } = useUnauthorizedModal();
  const { isAnonymous } = useAuthUser();

  const onImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.naturalWidth === 0) {
      setShowImage(cardImage);
    }
  }, []);

  const onImageError = useCallback(() => {
    setShowImage(cardImage);
  }, []);

  const onBellClick = useCallback(() => {
    if (!scheduledShow?.id) {
      return;
    }
  }, [scheduledShow?.id]);

  const goToShow = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isAvatarClick =
        target.closest('[data-avatar]') || target.closest('button');

      if (isAvatarClick) {
        return;
      }

      if (isScheduled && !isHost) {
        return;
      }

      if (
        (!!currentShowId && currentShowId !== showId && !isScheduled) ||
        (!!currentRecordedShowId &&
          currentRecordedShowId !== showId &&
          !isScheduled)
      ) {
        showModal();
        return;
      }

      navigate(APP_ROUTES.SHOWS_INITIALIZATION, { state: { id: showId } });
    },
    [
      showId,
      isHost,
      isScheduled,
      currentShowId,
      currentRecordedShowId,
      navigate,
      showModal,
    ],
  );

  const onConfirm = useCallback(() => {
    if (currentShowId) {
      onFinishOrLeaveCurrentShow();
    }

    if (currentRecordedShowId) {
      closeCurrentRecordedShow();
    }
    setIsWaitingNavigation(true);
    hideModal();
  }, [
    hideModal,
    currentShowId,
    currentRecordedShowId,
    onFinishOrLeaveCurrentShow,
    closeCurrentRecordedShow,
  ]);

  const goToChannel = useCallback(() => {
    openDownloadAppModal();
    // navigate(`${APP_ROUTES.CHANNELS}/${channelId}`);
  }, [openDownloadAppModal]);

  const goToProfile = useCallback(
    (id: string) => {
      if (isAnonymous) {
        return openUnauthorizedModal();
      }
      navigate(`${APP_ROUTES.PROFILE}/${id}`);
    },
    [navigate, openUnauthorizedModal, isAnonymous],
  );

  useEffect(() => {
    if (!currentShowId && !currentRecordedShowId && isWaitingNavigation) {
      setIsWaitingNavigation(false);
      navigate(APP_ROUTES.SHOWS_INITIALIZATION, { state: { id: showId } });
    }
  }, [
    currentShowId,
    currentRecordedShowId,
    isWaitingNavigation,
    navigate,
    showId,
  ]);

  return (
    <Styled.Container
      onClick={goToShow}
      $disabled={status === ShowStatus.Scheduled && !isHost}
    >
      <Styled.ImageContainer>
        <Styled.ShowImage
          src={showImage}
          onImageLoad={onImageLoad}
          onImageError={onImageError}
          alt='Image'
        />

        {isScheduled && !!scheduledShow?.startAt && (
          <Styled.CountdownContainer>
            <Countdown
              startTime={scheduledShow.startAt}
              backgroundFill={`${theme.colors.black}${theme.opacity[60]}`}
            />
          </Styled.CountdownContainer>
        )}

        <Styled.TopImageContainerPart>
          <Styled.ParticipantsContainer>
            {!isRecord &&
              [rightSpeakerProfileId, leftSpeakerProfileId].map((id) => {
                if (id) {
                  return (
                    <Avatar
                      key={id}
                      userId={id}
                      size={AvatarSize.Small}
                      onClick={() => goToProfile(id)}
                    />
                  );
                }
                return null;
              })}
          </Styled.ParticipantsContainer>

          {status === ShowStatus.Live ? (
            <Styled.LiveNow animationData={liveAnimation} loop autoplay />
          ) : isRecord ? (
            <SkewedLabel>
              <Styled.RedCircle />

              <Styled.RecText>REC</Styled.RecText>
            </SkewedLabel>
          ) : isScheduled && hostId !== currentUser?.id ? (
            <Styled.BellButton
              onClick={onBellClick}
              $isSubscribed={scheduledShow?.isSubscribed}
            >
              {scheduledShow?.isSubscribed ? (
                <Bell size={24} color={theme.colors.white} />
              ) : (
                <BellOff size={24} color={theme.colors.white} />
              )}
            </Styled.BellButton>
          ) : null}
        </Styled.TopImageContainerPart>

        <Styled.BottomImageContainerPart>
          <div />

          {isRecord && duration ? (
            <SkewedLabel key={showId}>{duration}</SkewedLabel>
          ) : isScheduled ? (
            scheduledShow?.startAt && (
              <SkewedLabel key={showId}>
                {getDateTimeString(new Date(scheduledShow?.startAt))}
              </SkewedLabel>
            )
          ) : null}
        </Styled.BottomImageContainerPart>
      </Styled.ImageContainer>

      {type !== ShowType.Sideless && (
        <Sides blueSide={blueSide} redSide={redSide} />
      )}

      <Styled.MainInfoContainer>
        <Styled.HostsContainer>
          <Styled.HostAvatarWrapper>
            <Avatar
              userId={hostId}
              size={AvatarSize.Small}
              onClick={() => goToProfile(hostId)}
            />
          </Styled.HostAvatarWrapper>

          {isShowCoHost && coHostId && (
            <Styled.CoHostAvatarWrapper>
              <Avatar
                userId={coHostId}
                size={AvatarSize.Small}
                onClick={() => goToProfile(coHostId)}
              />
            </Styled.CoHostAvatarWrapper>
          )}
        </Styled.HostsContainer>

        <Styled.TitleContainer>
          <Styled.TitleWithChannelContainer>
            <Styled.TitleWrapper>
              <Styled.TitleText>{title}</Styled.TitleText>

              <Styled.SubTitleContainer>
                <UserNameBlock
                  name={`${hostProfileData?.name || 'Unknown'}`}
                  verifiedAs={hostProfileData?.verifiedAs}
                />

                {isShowCoHost && (
                  <>
                    <Styled.NamesDivider>&</Styled.NamesDivider>

                    <UserNameBlock
                      name={`${coHostProfileData?.name || 'Unknown'}`}
                      verifiedAs={coHostProfileData?.verifiedAs}
                    />
                  </>
                )}
              </Styled.SubTitleContainer>
            </Styled.TitleWrapper>

            {!!channelId && !!channelData && (
              <Styled.ChannelButton onClick={goToChannel}>
                <Styled.ChannelImage src={channelData.image} alt='Channel' />
              </Styled.ChannelButton>
            )}
          </Styled.TitleWithChannelContainer>

          {!isScheduled && (
            <Styled.InfoContainer>
              <Styled.SubTitleText>
                {getListenersText(listenersCount)}
              </Styled.SubTitleText>

              {isRecord && !!createdDate && (
                <>
                  <Styled.Ellipse />

                  <Styled.SubTitleText>
                    {`${
                      createdDate ? formatDistanceToNow(createdDate) : ''
                    } ago`}
                  </Styled.SubTitleText>
                </>
              )}
            </Styled.InfoContainer>
          )}
        </Styled.TitleContainer>
      </Styled.MainInfoContainer>

      <Modal
        isShown={isModalShown}
        onClose={hideModal}
        onConfirm={onConfirm}
        isCloseable
      >
        <PremierText>
          Are you sure you want to
          {!isHost || currentRecordedShowId ? ' exit' : ' end'} current show?
        </PremierText>
      </Modal>
    </Styled.Container>
  );
};

export default memo(ShowCard);
